import { useEffect, useRef, useState } from 'react'
import axios from 'axios'

import { apiURL } from 'src/consumer/services'

/*
 * useContactNumber
 *
 * Single source of truth for AUMHUM's public contact number. Returns a
 * sensible default synchronously so the UI never blocks on the network,
 * and asynchronously upgrades to whatever the server publishes at
 * `/api/public/contact-info` (if that endpoint exists).
 *
 * Consumers get a small normalised shape they can drop into `tel:`,
 * `https://wa.me/…`, or a display label without doing their own
 * string munging.
 */

const DEFAULT_E164 = '+916360903465'
const DEFAULT_DISPLAY = '+91 63609 03465'

export const DEFAULT_CONTACT = buildContact({
  e164: DEFAULT_E164,
  display: DEFAULT_DISPLAY,
  source: 'default',
})

export function useContactNumber() {
  const [contact, setContact] = useState(DEFAULT_CONTACT)
  const didFetchRef = useRef(false)

  useEffect(() => {
    if (didFetchRef.current) return undefined
    didFetchRef.current = true

    let cancelled = false
    axios
      .get(`${apiURL}/api/public/contact-info`, { timeout: 4500 })
      .then((res) => {
        if (cancelled) return
        const remote =
          res?.data?.data?.contact ||
          res?.data?.contact ||
          null
        if (!remote || !remote.e164) return
        const upgraded = buildContact({
          e164: remote.e164,
          display: remote.display,
          source: 'remote',
        })
        if (!upgraded) return
        // Only swap if the remote number is actually usable.
        setContact(upgraded)
      })
      .catch(() => {
        // Silent — the default is already in state.
      })
    return () => {
      cancelled = true
    }
  }, [])

  return contact
}

function buildContact({ e164, display, source }) {
  if (!e164) return null
  const trimmed = String(e164).trim()
  const digits = trimmed.replace(/[^0-9]/g, '')
  if (digits.length < 7) return null
  const e164Normalised = trimmed.startsWith('+') ? trimmed : `+${digits}`
  const shown = display && String(display).trim().length > 0 ? display : e164Normalised
  return {
    e164: e164Normalised,
    digits,
    display: shown,
    telHref: `tel:${e164Normalised}`,
    whatsappHref: `https://wa.me/${digits}`,
    source: source || 'default',
  }
}
